import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { formatDate, formatReadCount, getArticleById, getCategory, getCategoryName, getRelatedArticles } from '@/utils/news';
import { useSeo } from '@/hooks/useSeo';

export default function DetailPage() {
  const { newsId } = useParams<{ newsId: string }>();
  const article = getArticleById(newsId);
  const [copied, setCopied] = useState(false);

  useSeo({
    title: article ? `${article.title} - 9·1牛网` : '内容不存在 - 9·1牛网',
    description: article ? article.summary : '该内容不存在或已删除。',
    keywords: article ? `${article.tags.join(',')},${getCategoryName(article.category)},9·1牛网` : '9·1牛网',
  });

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 w-full px-4 md:px-8 py-20 flex flex-col items-center justify-center text-center">
          <i className="ri-error-warning-line text-4xl text-accent-500 mb-4" />
          <h1 className="font-heading font-bold text-xl text-foreground-950">内容不存在或加载失败</h1>
          <p className="mt-2 text-sm text-foreground-500">该文章可能已被删除，或链接地址有误。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-4-line" /> 返回首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const category = getCategory(article.category);
  const related = getRelatedArticles(article, 5);
  const shareUrl = encodeURIComponent(`https://bj-weixiu.cn/detail/${article.id}`);
  const shareTitle = encodeURIComponent(article.title);

  const shares = [
    { icon: 'ri-wechat-line', label: '微信', href: '#' },
    { icon: 'ri-weibo-line', label: '微博', href: `https://service.weibo.com/share/share.php?url=${shareUrl}&title=${shareTitle}` },
    { icon: 'ri-qq-line', label: 'QQ', href: `https://connect.qq.com/widget/shareqq/index.html?url=${shareUrl}&title=${shareTitle}` },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`https://bj-weixiu.cn/detail/${article.id}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1 w-full px-4 md:px-8 py-6 md:py-8">
        <Breadcrumb items={[{ label: `${getCategoryName(article.category)}频道`, to: `/category/${article.category}` }, { label: article.title }]} />

        <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-10">
          <article className="lg:col-span-2">
            <div className="mb-4">
              <span className="inline-block px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800">
                {getCategoryName(article.category)}
              </span>
            </div>
            <h1 className="font-heading font-black text-2xl md:text-4xl text-foreground-950 leading-snug">
              {article.title}
            </h1>

            <div className="mt-4 flex items-center gap-4 text-xs md:text-sm text-foreground-500 flex-wrap pb-5 border-b border-background-200">
              <span className="inline-flex items-center gap-1">
                <i className="ri-user-3-line" />
                {article.author}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-time-line" />
                {formatDate(article.publishTime)}
              </span>
              <span className="inline-flex items-center gap-1">
                <i className="ri-fire-line" />
                {formatReadCount(article.readCount)} 阅读
              </span>
              {category && (
                <Link to={`/category/${category.key}`} className="text-secondary-600 hover:text-primary-600 transition-colors cursor-pointer">
                  #{category.name}
                </Link>
              )}
            </div>

            <div className="mt-6 w-full h-56 md:h-96 rounded-lg overflow-hidden bg-background-100">
              <img
                src={article.cover}
                alt={`${article.title} 封面图`}
                title={`${article.title} - 9·1牛网`}
                className="w-full h-full object-top object-cover"
              />
            </div>

            <div className="mt-6 md:mt-8 max-w-2xl">
              {article.content.map((block, i) => {
                if (block.type === 'heading') {
                  return (
                    <h2 key={i} className="font-heading font-bold text-lg md:text-xl text-foreground-950 mt-7 mb-3">
                      {block.text}
                    </h2>
                  );
                }
                if (block.type === 'image') {
                  return (
                    <figure key={i} className="my-6">
                      <div className="w-full h-48 md:h-72 rounded-lg overflow-hidden bg-background-100">
                        <img
                          src={block.src}
                          alt={block.caption || `${article.title} 配图`}
                          title={`${article.title} - 9·1牛网`}
                          className="w-full h-full object-top object-cover"
                        />
                      </div>
                      {block.caption && (
                        <figcaption className="mt-2 text-center text-xs md:text-sm text-foreground-500">{block.caption}</figcaption>
                      )}
                    </figure>
                  );
                }
                return (
                  <p key={i} className="text-sm md:text-base text-foreground-700 leading-loose mb-4">
                    {block.text}
                  </p>
                );
              })}
            </div>

            <div className="mt-8 flex flex-wrap gap-2">
              {article.tags.map((t) => (
                <span key={t} className="px-3 py-1 rounded-full text-xs font-medium bg-background-200 text-foreground-700">
                  #{t}
                </span>
              ))}
            </div>

            <div className="mt-8 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
              <p className="text-sm font-medium text-foreground-800 inline-flex items-center gap-2">
                <i className="ri-share-forward-line text-primary-600" /> 分享这篇文章
              </p>
              <div className="flex items-center gap-2">
                {shares.map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`分享到${s.label}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className={s.icon} />
                    {s.label}
                  </a>
                ))}
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-50 border border-background-300 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className={copied ? 'ri-check-line text-primary-600' : 'ri-link'} />
                  {copied ? '已复制' : '复制链接'}
                </button>
              </div>
            </div>
          </article>

          <aside className="lg:col-span-1">
            <div className="lg:sticky lg:top-24 rounded-lg bg-background-100 border border-background-200 p-4 md:p-5">
              <h2 className="font-heading font-bold text-base text-foreground-950 mb-4 inline-flex items-center gap-2">
                <i className="ri-links-line text-primary-600" /> 相关推荐
              </h2>
              {related.length === 0 ? (
                <p className="text-sm text-foreground-500">暂无相关文章</p>
              ) : (
                <ol className="space-y-3">
                  {related.map((r, i) => (
                    <li key={r.id}>
                      <Link to={`/detail/${r.id}`} className="group flex gap-3 cursor-pointer">
                        <span className="w-5 h-5 shrink-0 rounded-md bg-background-200 text-foreground-600 flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </span>
                        <div className="min-w-0">
                          <p className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors line-clamp-2">
                            {r.title}
                          </p>
                          <p className="text-xs text-foreground-500 mt-1">{formatDate(r.publishTime)}</p>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ol>
              )}
              {category && (
                <Link
                  to={`/category/${category.key}`}
                  className="mt-5 inline-flex w-full items-center justify-center gap-1 px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  进入{category.name}频道
                  <i className="ri-arrow-right-line" />
                </Link>
              )}
            </div>
          </aside>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}